import { readMarketingConsent } from '@/components/render-layout/components/marketing-consent';

declare global {
  interface Window {
    __flushFbqQueue?: () => void;
    __flushGtagQueue?: () => void;
    __flushOaiqQueue?: () => void;
  }
}

/** The window hook each Marketing Tag's inline init script calls once its global exists. */
type FlushHook = '__flushFbqQueue' | '__flushGtagQueue' | '__flushOaiqQueue';

/**
 * Holds the calls made before a `lazyOnload` Marketing Tag's init script has
 * run, so a fast form submit, the first page view or a visit to a Conversion
 * Page is sent late rather than dropped. `enqueue` hangs `flush` on the window
 * under `flushHook`, and the tag's init script calls it straight after
 * defining its global (see `meta-pixel.tsx`, `google-tag-script.tsx`,
 * `openai-pixel.tsx`).
 *
 * A call is held only once the visitor has given Marketing Consent, the one
 * state in which a Marketing Tag loads. Without consent it is dropped: held, it
 * would be sent if the visitor accepted later on the same page, as though it
 * had been collected with consent (for Google, under the tag's granted consent
 * update). `enqueue` says which happened: `true` when the call is held,
 * `false` when it is dropped.
 */
export function createScriptLoadQueue<T>({ flushHook, send }: { flushHook: FlushHook; send: (item: T) => void }) {
  const queue: T[] = [];

  function flush() {
    while (queue.length > 0) {
      const item = queue.shift();
      if (item !== undefined) send(item);
    }
  }

  function enqueue(item: T): boolean {
    if (typeof window === 'undefined' || readMarketingConsent() !== 'given') return false;
    if (!window[flushHook]) {
      window[flushHook] = flush;
    }
    queue.push(item);
    return true;
  }

  return { queue, flush, enqueue };
}
